// This file is responsible for making and caching requests to the Google Maps Directions API.
//  It returns the snapped coordinates and road-aligned path for a given route.
//
// Ambassador: Yatharth.
//
import _ from 'lodash';
import { useCallback, useMemo } from 'react';

import {
    DirectionsRequest,
    DirectionsResult,
    Maps,
    formatLatLangForDirections,
} from './googleMapTypes';
import { LatLngStr, Route } from './stopTypes';

// Global cache for all Directions Service requests.
const cache: Record<string, DirectionsResult> = {};

export const useFetchRoute = ({ maps }: { maps: Maps | null }) => {
    //

    const directionsService = useMemo(() => {
        if (!maps) return null;
        return new maps.DirectionsService();
    }, [maps]);

    const fetchRoute = useCallback(
        (route: Route, callback: (result: DirectionsResult) => void) => {
            //

            // Check cache.
            const cacheKey = JSON.stringify(route);
            if (cacheKey in cache) {
                callback(cache[cacheKey]);
                return;
            }

            // Otherwise, prepare a directions request.
            const origin = formatLatLangForDirections(route[0]);
            const destination = formatLatLangForDirections(route[route.length - 1]);
            const waypoints = route.slice(1, route.length - 1).map((stop) => ({
                location: formatLatLangForDirections(stop),
                stopover: true,
            }));
            const directionsRequest: DirectionsRequest = {
                origin,
                destination,
                waypoints,
                travelMode: 'DRIVING',
                optimizeWaypoints: false,
            };

            // If the directions service is not ready, we can't do anything.
            if (directionsService === null) return;

            // Issue the directions request, and cache the result.
            directionsService.route(directionsRequest, (result, status) => {
                if (status !== 'OK') return;
                cache[cacheKey] = result;
                callback(result);
            });

            //
        },
        [directionsService],
    );

    return fetchRoute;
};

// Get the snapped coordinates of the pin markers for a given directions result.
export const snappedLatLngsFromResult = (result: DirectionsResult): LatLngStr[] => {
    const legs = result.routes[0].legs;
    const startsOfEachLeg = _.map(legs, (leg) => leg.start_location);
    const endsOfEachLeg = _.map(legs, (leg) => leg.end_location);
    const legMarkers = [startsOfEachLeg[0], ...endsOfEachLeg];
    return legMarkers.map((legMarker) => ({
        lat: legMarker.lat().toString(),
        lng: legMarker.lng().toString(),
    }));
};
